import { fetchEventSource } from '@microsoft/fetch-event-source';
import {
  getAuthHeaders,
  resolvePeriverseApiBaseUrl,
  tokenStore,
} from '../shared';
import {
  RealtimeConnectionEventSchema,
  RealtimeEventEnvelopeSchema,
} from '@peritasai/types';
import type {
  EventSubscription,
  RealtimeEvent,
  RealtimeSubscriptionRequest,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

interface HandshakeValidationResult {
  isValid: boolean;
  contentType: string | null;
}

const logDebug = (...args: unknown[]) => {
  console.log('[TEMP-SSE-DEBUG][RealtimeAPI]', ...args);
};

/**
 * Callback function for handling real-time events
 */
export type RealtimeEventCallback = (event: RealtimeEvent) => void;

/**
 * Connection state for SSE
 */
export interface RealtimeConnectionState {
  /** Whether the connection is currently connected */
  connected: boolean;
  /** Error message if connection failed */
  error: Error | null;
}

/**
 * Options for connecting to real-time stream
 */
export interface RealtimeConnectionOptions {
  /** Subscriptions array */
  subscriptions: EventSubscription[];
  /** Callback function for events */
  onEvent: RealtimeEventCallback;
  /** Callback for connection state changes */
  onStateChange?: (state: RealtimeConnectionState) => void;
  /** AbortController signal for cancellation */
  signal?: AbortSignal;
}

/**
 * Service for managing Server-Sent Events (SSE) connections
 * to the real-time updates endpoint
 */
export class RealtimeService {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  /**
   * Connect to the real-time stream endpoint.
   * Callers must abort the signal to close; fetch-event-source dispose() aborts the fetch on error/throw.
   * Close old connections before opening new ones to avoid socket leaking (see realtime-hooks delay).
   */
  async connect(options: RealtimeConnectionOptions): Promise<void> {
    const { subscriptions, onEvent, onStateChange, signal } = options;

    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/realtime/stream`;
    logDebug('connect:begin', {
      url,
      subscriptions,
      hasSignal: !!signal,
      signalAborted: !!signal?.aborted,
    });

    const requestBody: RealtimeSubscriptionRequest = {
      subscriptions,
    };

    const BASE_RETRY_DELAY_MS = 1000;
    const validateHandshake = (
      status: number,
      contentTypeHeader: string | null
    ): HandshakeValidationResult => {
      const allowedStatuses = [200, 201];
      const contentType = contentTypeHeader?.toLowerCase() ?? null;
      const isValid =
        allowedStatuses.includes(status) &&
        !!contentType &&
        contentType.includes('text/event-stream');

      return { isValid, contentType };
    };

    let connectionState: RealtimeConnectionState = {
      connected: false,
      error: null,
    };

    const updateState = (updates: Partial<RealtimeConnectionState>) => {
      connectionState = { ...connectionState, ...updates };
      onStateChange?.(connectionState);
    };

    // Track retry count to prevent infinite retries
    let retryCount = 0;
    const MAX_RETRIES = 3;

    // Synchronous - fetch-event-source does not await onerror; it uses the return value for retry delay.
    // Return delayMs to let the library handle the sleep; throw for fatal errors to stop retries.
    const handleError = (err: any): number => {
      const isAbort =
        signal?.aborted ||
        (err instanceof Error && err.name === 'AbortError') ||
        /abort/i.test(String(err ?? ''));

      // Expected during cleanup/re-init; do not publish error state or logs.
      if (isAbort) {
        logDebug('connect:error-abort', {
          signalAborted: !!signal?.aborted,
          errorName: err instanceof Error ? err.name : null,
          errorMessage: err instanceof Error ? err.message : String(err ?? ''),
        });
        throw err;
      }

      const errorMessage =
        err instanceof Error ? err.message : String(err ?? 'Unknown error');
      const isQuic = /quic/i.test(errorMessage);
      const isNetworkError = /network error/i.test(errorMessage);

      // Provide protocol-aware diagnostics for ops visibility.
      if (isQuic) {
        console.error('SSE QUIC protocol issue detected:', err);
      } else if (isNetworkError) {
        console.error(
          'SSE network error detected (likely dropped connection):',
          err
        );
      } else {
        console.error('SSE error:', err);
      }

      updateState({ connected: false, error: err as Error });

      const is401 = /401|Unauthorized/i.test(errorMessage);
      if (is401) {
        console.warn(
          'SSE handshake failed with 401, stopping (token expired or invalid)'
        );
        throw err;
      }

      retryCount++;
      if (retryCount >= MAX_RETRIES) {
        console.error(
          `Max retries (${MAX_RETRIES}) exceeded, stopping connection`
        );
        throw err;
      }

      // Retriable: return delay so library handles setTimeout(create, delayMs)
      const backoffDelay = BASE_RETRY_DELAY_MS * Math.pow(2, retryCount - 1);
      const jitter = Math.floor(Math.random() * 200);
      const delayMs = backoffDelay + jitter;
      console.warn(`Retrying in ${delayMs} ms...`);
      return delayMs;
    };

      const connectWithAuth = async () => {
      logDebug('connect:with-auth:start');
      const authHeaders = await getAuthHeaders();
      if (!authHeaders['Authorization']) {
        const noTokenError = new Error(
          'No auth token available for SSE connection'
        );
        logDebug('connect:with-auth:no-token');
        updateState({ connected: false, error: noTokenError });
        return;
      }
      logDebug('connect:with-auth:token-present');
      return fetchEventSource(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...authHeaders,
        },
        body: JSON.stringify(requestBody),
        signal,
        async onopen(response) {
          const contentTypeHeader = response.headers.get('content-type');
          const { isValid, contentType } = validateHandshake(
            response.status,
            contentTypeHeader
          );
          logDebug('connect:onopen', {
            status: response.status,
            contentTypeHeader,
            isValid,
          });

          // Accept 200 OK and 201 Created responses as long as the content type is the SSE MIME type.
          // If connections keep dropping, consider raising GKE BackendConfig timeoutSec (>60s recommended) to reduce churn.
          if (isValid) {
            retryCount = 0; // Connection established
            updateState({ connected: true, error: null });
            return; // Connection successful
          }

          // Explicitly reject non-SSE or unexpected status codes to avoid silent QUIC/protocol failures downstream.
          const error = new Error(
            `Realtime handshake failed: status=${
              response.status
            } content-type=${contentType ?? 'none'}`
          );
          updateState({ connected: false, error });
          throw error;
        },
        onmessage(event) {
          try {
            if (!event.data || !event.data.trim()) {
              // GCLB Heartbeat Filter: skip empty heartbeat frames used to keep the connection alive.
              return;
            }

            const raw: unknown = JSON.parse(event.data);

            // Filter out connection events (they have connectionId and message, not type/timestamp/data)
            if (RealtimeConnectionEventSchema.safeParse(raw).success) {
              return;
            }

            // Validate that this is a proper RealtimeEvent envelope
            const envelope = RealtimeEventEnvelopeSchema.safeParse(raw);
            if (!envelope.success) {
              console.warn(
                'Received event missing required fields (type/timestamp):',
                raw
              );
              logDebug('connect:onmessage:invalid-shape', raw);
              return;
            }

            const data = raw as RealtimeEvent;
            retryCount = 0; // Only reset retries after receiving a real event, indicating a stable stream.
            logDebug('connect:onmessage:event', {
              type: data.type,
              timestamp: data.timestamp,
            });
            onEvent(data);
          } catch (error) {
            console.error('Error parsing SSE event:', {
              error,
              rawEventData: event.data,
              eventId: event.id,
              eventType: event.event,
            });
            // Don't throw - continue processing other events
          }
        },
        onerror: handleError,
        onclose() {
          if (signal?.aborted) {
            logDebug('connect:onclose:aborted');
            updateState({ connected: false, error: null });
            return;
          }

          const closeError = new Error('Realtime stream closed unexpectedly');
          logDebug('connect:onclose:unexpected');
          updateState({ connected: false, error: closeError });
        },
      });
    };

    let is401RefreshAttempted = false;

    while (true) {
      try {
        logDebug('connect:attempt', {
          retryCount,
          is401RefreshAttempted,
        });
        await connectWithAuth();
        logDebug('connect:completed');
        return;
      } catch (error) {
        // Handle abort errors gracefully
        if (error instanceof Error && error.name === 'AbortError') {
          logDebug('connect:abort-caught');
          updateState({ connected: false, error: null });
          return;
        }

        // One-time refresh on 401 (tokenStore.refresh deduplicates if multiple hooks hit 401)
        const is401 =
          error instanceof Error &&
          /401|Unauthorized/i.test(error.message);
        if (is401 && !is401RefreshAttempted) {
          is401RefreshAttempted = true;
          logDebug('connect:401-refresh:start');
          const newToken = await tokenStore.refresh();
          if (newToken) {
            logDebug('connect:401-refresh:success');
            continue; // Retry connectWithAuth with fresh token
          }
          logDebug('connect:401-refresh:failed');
        }

        // Other errors or refresh failed
        logDebug('connect:failed', {
          errorName: error instanceof Error ? error.name : null,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        updateState({
          connected: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
        throw error;
      }
    }
  }
}

/**
 * Create a RealtimeService instance
 */
export const createRealtimeService = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): RealtimeService => new RealtimeService(resolveBaseUrl);

/**
 * Default singleton instance
 */
export const realtimeService = new RealtimeService();
